import { usePerms } from 'contexts/PermsContext';
import { useMemo, useState } from 'react';
import { Button, Container } from 'react-bootstrap';
import { FaRedo } from 'react-icons/fa';
import { useDispatch } from 'react-redux';
import { auditLogsV2Api, useGetAuditLogsQuery } from 'services/auditLogs_v2/auditLogs_v2.api';
import AuditLogFiltersBar from './components/AuditLogFiltersBar';
import AuditLogTable from './components/AuditLogTable';
import { AuditLogFilters, buildAuditLogParams } from './utils/searchBuilder';

export const DEFAULT_FILTERS: AuditLogFilters = {
  page: 1,
  pageSize: 10,
  orderBy: 'created_at',
  orderDir: 'DESC',
};

export default function AuditLogsV2() {
  const dispatch = useDispatch();
  // committedFilters drives the RTK query — only updates on Search button click or pagination/sort change
  const [committedFilters, setCommittedFilters] = useState<AuditLogFilters>(DEFAULT_FILTERS);
  const perms = usePerms();
  const canViewLogs = perms?.testPermission([8, 10, 11, 12, 13], ['ViewAuditLog']);

  const params = useMemo(() => buildAuditLogParams(committedFilters), [committedFilters]);

  const { data, refetch, isLoading, isFetching: isFeatchingLogs, isError } = useGetAuditLogsQuery(params, { skip: !canViewLogs });

  const handleFullRefresh = () => {
    dispatch(auditLogsV2Api.util.invalidateTags(['AuditLog']));
    refetch();
  };


  return (
    <Container fluid className="py-3 overflow-auto" style={{ minHeight: '60vh' }}>
      <div className="d-flex flex-column gap-3">
        {/* Header, Refresh, and Filters Bar in one row */}
        <div className="w-100 position-relative">
          <div className="d-flex flex-row align-items-start justify-content-between gap-3 d-none d-sm-flex">
            <div className="d-flex align-items-start gap-2">
              <h2 className="fs-4 mb-0">Audit Logs</h2>
              <Button
                type="button"
                variant="dark"
                className=''
                disabled={isFeatchingLogs}
                onClick={handleFullRefresh}
              >
                <FaRedo className='mb-1 mt-1' title='Refresh' size={16} />
              </Button>
            </div>
            {/* Filters Bar at far right for sm and up */}
            <div className="d-flex justify-content-end align-items-end" style={{ flex: 1 }}>
              <AuditLogFiltersBar
                committedFilters={committedFilters}
                isFeatchingLogs={isFeatchingLogs}
                onSearch={(filters) => setCommittedFilters({ ...filters, page: 1 })}
              />
            </div>
          </div>
          {/* Filters Bar below title for xs screens */}
          <div className="d-block d-sm-none mt-3">
            <div className="d-flex flex-row align-items-center gap-2 mb-2 w-sm-100 justify-content-center">
              <h2 className="fs-4 mb-0 text-center">Audit Logs</h2>
              <Button
                type="button"
                variant="dark"
                className='justify-content-start'
                disabled={isFeatchingLogs}
                onClick={handleFullRefresh}
              >
                <FaRedo className='mb-1 mt-1' title='Refresh' size={16} />
              </Button>
            </div>
            <AuditLogFiltersBar
              committedFilters={committedFilters}
              isFeatchingLogs={isFeatchingLogs}
              onSearch={(filters) => setCommittedFilters({ ...filters, page: 1 })}
            />
          </div>
        </div>

        {/* Table */}
        {canViewLogs ? (
          <AuditLogTable
            data={data}
            isLoading={isLoading}
            isError={isError}
            filters={committedFilters}
            onFiltersChange={setCommittedFilters}
          />
        ) : (
          <div className="text-center fw-bold mt-5">You do not have permission to view audit logs.</div>
        )}
      </div>
    </Container>
  );
}
